// apps/web/src/pages/WrongQuestionsPage.tsx
// 错题本 — Phase 4 实现

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { clearWrongApi, listWrongApi } from "@api/userData";
import type { WrongQuestionDTO } from "@examfast/shared-types";

const SETTINGS_STORAGE_KEY = "examfast.practice.settings";
const DEFAULT_WRONG_REPEAT_THRESHOLD = 3;

export default function WrongQuestionsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bankId = searchParams.get("bankId") ?? undefined;
  const [showManager, setShowManager] = useState(false);
  const [wrongRepeatThreshold, setWrongRepeatThreshold] = useState(() => loadWrongRepeatThreshold());

  const { data, isLoading, error } = useQuery({
    queryKey: ["wrong", bankId],
    queryFn: () => listWrongApi({ page_size: 50, bank_id: bankId }),
  });

  const clearMut = useMutation({
    mutationFn: () => clearWrongApi({ bank_id: bankId }),
    onSuccess: () => {
      toast.success("错题已清空");
      setShowManager(false);
      queryClient.invalidateQueries({ queryKey: ["wrong"] });
    },
    onError: () => toast.error("清空失败"),
  });

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">加载失败，请重试</div>
    );
  }

  const items = data?.items ?? [];
  const groups = groupByQuestionType(items.map((item) => item.question));
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayCount = items.filter((item) => item.last_wrong_at >= todayStart.getTime()).length;
  const totalWrongAttempts = items.reduce((sum, item) => sum + item.wrong_count, 0);
  const wrongRate = totalWrongAttempts > 0 ? Math.round((items.length / totalWrongAttempts) * 100) : 0;
  const firstQuestion = items[0]?.question;
  const startWrongPractice = (questionId?: string) => {
    const target = questionId ? items.find((item) => item.question_id === questionId)?.question : firstQuestion;
    if (!target) return;
    navigate(`/banks/${target.bank_id}/practice/wrong${questionId ? `?questionId=${questionId}` : ""}`);
  };

  return (
    <div className="min-h-screen bg-white pb-8">
      <header className="bg-gradient-to-b from-red-500 to-red-400 text-white">
        <div className="relative flex h-14 items-center justify-center px-4">
          <button onClick={() => navigate(-1)} className="absolute left-3 flex h-10 w-10 items-center justify-center" aria-label="返回">
            <ChevronLeftIcon />
          </button>
          <h1 className="text-lg font-semibold">{bankId ? "题库错题" : "我的错题"}</h1>
          <div className="absolute right-3 flex h-9 items-center rounded-full bg-red-700/30 px-3">
            <span className="text-lg leading-none">•••</span>
            <span className="mx-3 h-5 w-px bg-white/20" />
            <span className="h-5 w-5 rounded-full border-[5px] border-white" />
          </div>
        </div>
        <div className="px-6 pb-8 pt-8 text-center">
          <p className="text-xl font-semibold">错误率</p>
          <p className="mt-5 text-7xl font-light leading-none">
            {wrongRate}<span className="text-2xl">%</span>
          </p>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p>暂无错题</p>
          <p className="text-sm mt-1">做错题目后会自动收录</p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 border-b border-gray-100 bg-white text-center">
            <button onClick={() => startWrongPractice()} className="border-r border-gray-100 py-7">
              <p className="text-3xl font-semibold text-red-500">{items.length}<span className="text-sm text-gray-400">题</span></p>
              <p className="mt-3 text-lg font-semibold text-gray-900">全部错题 ›</p>
            </button>
            <button onClick={() => startWrongPractice(items[0]?.question_id)} className="py-7">
              <p className="text-3xl font-semibold text-red-500">{todayCount}<span className="text-sm text-gray-400">题</span></p>
              <p className="mt-3 text-lg font-semibold text-gray-900">今日错题 ›</p>
            </button>
          </div>

          <section className="px-4 py-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">错题分布</h2>
              <button
                type="button"
                onClick={() => setShowManager(true)}
                className="text-base font-medium text-gray-400"
              >
                错题管理
              </button>
            </div>
            <div className="mb-4 flex gap-3">
              <span className="rounded-md bg-red-50 px-3 py-2 text-lg font-semibold text-red-500">题型分类</span>
              <span className="rounded-md bg-gray-100 px-3 py-2 text-lg font-semibold text-gray-900">章节分类</span>
              <span className="rounded-md bg-gray-100 px-3 py-2 text-lg font-semibold text-gray-900">错题次数</span>
            </div>

            <div className="divide-y divide-gray-100">
              {groups.map((group) => (
                <button
                  key={group.type}
                  type="button"
                  onClick={() => startWrongPractice(group.firstQuestionId)}
                  className="flex w-full items-center justify-between py-5 text-left"
                >
                  <span className="text-xl font-semibold text-gray-900">{group.label}</span>
                  <span className="text-lg font-semibold text-gray-400">{group.count}题 ›</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}

      {showManager && (
        <WrongManagerSheet
          threshold={wrongRepeatThreshold}
          isClearing={clearMut.isPending}
          onClose={() => setShowManager(false)}
          onThresholdChange={(value) => {
            setWrongRepeatThreshold(value);
            saveWrongRepeatThreshold(value);
          }}
          onClear={() => clearMut.mutate()}
        />
      )}
    </div>
  );
}

function WrongManagerSheet({
  threshold,
  isClearing,
  onClose,
  onThresholdChange,
  onClear,
}: {
  threshold: number;
  isClearing: boolean;
  onClose: () => void;
  onThresholdChange: (value: number) => void;
  onClear: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/70">
      <div className="w-full rounded-t-3xl bg-white px-6 pb-8 pt-6">
        <div className="relative mb-8 flex items-center justify-center">
          <h2 className="text-2xl font-semibold text-gray-900">错题管理</h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 flex h-10 w-10 items-center justify-center text-gray-400"
            aria-label="关闭"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex min-h-[64px] items-center justify-between border-b border-gray-100">
          <span className="text-xl font-medium text-gray-900">答对几次，移出我的错题</span>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => onThresholdChange(Math.max(0, threshold - 1))}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-2xl text-gray-700"
            >
              -
            </button>
            <span className="w-6 text-center text-xl font-semibold text-gray-900">{threshold}</span>
            <button
              type="button"
              onClick={() => onThresholdChange(Math.min(20, threshold + 1))}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-2xl text-gray-700"
            >
              +
            </button>
          </div>
        </div>

        <button
          type="button"
          disabled={isClearing}
          onClick={() => {
            if (window.confirm("确定清空错题吗？")) onClear();
          }}
          className="flex min-h-[64px] w-full items-center border-b border-gray-100 text-left text-xl font-medium text-gray-900 disabled:opacity-50"
        >
          {isClearing ? "清空中..." : "清空错题"}
        </button>
      </div>
    </div>
  );
}

function getTypeLabel(type: string): string {
  const map: Record<string, string> = {
    single_choice: "单选",
    multiple_choice: "多选",
    true_false: "判断",
    fill_blank: "填空",
    essay: "简答",
  };
  return map[type] ?? type;
}

function groupByQuestionType(questions: WrongQuestionDTO["question"][]) {
  const map = new Map<string, { type: string; label: string; count: number; firstQuestionId: string }>();
  for (const question of questions) {
    const existing = map.get(question.type);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(question.type, {
        type: question.type,
        label: getTypeLabel(question.type),
        count: 1,
        firstQuestionId: question.id,
      });
    }
  }
  return [...map.values()];
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function loadWrongRepeatThreshold(): number {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_WRONG_REPEAT_THRESHOLD;
    const parsed = JSON.parse(raw) as { wrongRepeatThreshold?: number };
    return typeof parsed.wrongRepeatThreshold === "number" ? parsed.wrongRepeatThreshold : DEFAULT_WRONG_REPEAT_THRESHOLD;
  } catch {
    return DEFAULT_WRONG_REPEAT_THRESHOLD;
  }
}

function saveWrongRepeatThreshold(value: number) {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as Record<string, unknown> : {};
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({ ...parsed, wrongRepeatThreshold: value }));
  } catch {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({ wrongRepeatThreshold: value }));
  }
}
